import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '../../../themes/typography';
import { theme } from '../../../themes/technicalAlerts';

/**
 * AlertStats component - Displays product name and alert statistics
 * Shows product name as header and weeks, slots, revenue loss in a grid layout
 */
const AlertStats = ({ alert, productName, t }) => {
  // Don't render if no alert data
  if (!alert) {
    return null;
  }

  const weeks = alert.weeks_since || 3;
  const slots = Array.isArray(alert.selection_code) 
    ? alert.selection_code.slice(0, 2).join(', ') 
    : 'A1,A2';
  const revenueLoss = alert.revenue_loss || 450;

  const formatRevenue = (value) => `€${value}`;

  return (
    <Box sx={{ mb: '6px' }}>
      {/* Product name header - bold text outside container */}
      <Typography sx={{
        fontSize: '12px',
        color: colors.text.primary,
        fontWeight: 700,
        lineHeight: 1.2,
        mb: '6px',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}>
        {productName || alert.product_name}
      </Typography>
      
      {/* Stats container */}
      <Box sx={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        borderRadius: '8px',
        p: '6px',
        border: `1px solid ${colors.border.light}`,
        flex: 1
      }}>
      {/* Stats grid - 3 columns layout */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px' }}>
        {/* Weeks stat */}
        <Box sx={{ 
          textAlign: 'center',
          p: '4px',
          backgroundColor: 'rgba(248, 250, 252, 0.5)',
          borderRadius: '4px',
          border: '1px solid rgba(226, 232, 240, 0.4)',
          minWidth: 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, mb: '1px' }}>
            <Typography sx={{
              fontSize: '10px',
              fontWeight: 600,
              color: colors.text.primary,
              lineHeight: 1.2
            }}>
              {weeks}
            </Typography>
          </Box>
          <Typography sx={{
            fontSize: '7px',
            color: colors.text.primary,
            fontWeight: 500,
            lineHeight: 1,
            textTransform: 'uppercase'
          }}>
            {t ? t('weeksWithoutSales').toUpperCase() : 'WEEKS'}
          </Typography>
        </Box>
        
        {/* Slots stat */}
        <Box sx={{ 
          textAlign: 'center',
          p: '4px',
          backgroundColor: 'rgba(248, 250, 252, 0.5)',
          borderRadius: '4px',
          border: '1px solid rgba(226, 232, 240, 0.4)',
          minWidth: 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, mb: '1px' }}>
            <Typography sx={{
              fontSize: '10px',
              fontWeight: 600,
              color: colors.text.primary,
              lineHeight: 1.2
            }}>
              {slots}
            </Typography>
          </Box>
          <Typography sx={{
            fontSize: '7px',
            color: colors.text.primary,
            fontWeight: 500,
            lineHeight: 1,
            textTransform: 'uppercase'
          }}>
            {t ? t('slots').toUpperCase() : 'CARRILES'}
          </Typography>
        </Box>
        
        {/* Revenue loss stat */}
        <Box sx={{ 
          textAlign: 'center',
          p: '4px',
          backgroundColor: 'rgba(248, 250, 252, 0.5)',
          borderRadius: '4px',
          border: '1px solid rgba(226, 232, 240, 0.4)',
          minWidth: 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, mb: '1px' }}>
            <Typography sx={{
              fontSize: '10px',
              fontWeight: 600,
              color: theme.colors.alert, // Theme alert color for consistency
              lineHeight: 1.2
            }}>
              {formatRevenue(revenueLoss)}
            </Typography>
          </Box>
          <Typography sx={{
            fontSize: '7px',
            color: colors.text.primary,
            fontWeight: 500,
            lineHeight: 1,
            textTransform: 'uppercase'
          }}>
            {t ? t('estimatedLost').toUpperCase() : 'LOST'}
          </Typography>
        </Box>
      </Box>
      {/* End of stats container */}
    </Box>
    {/* End of main container */}
    </Box>
  );
};

export default AlertStats;